import { useEffect } from 'react';
import {
  Modal,
  Form,
  Row,
  Col,
  Space,
  Typography,
  Radio,
  Input,
  Divider,
  List,
  Flex,
} from 'antd';
import moment from 'moment';
import type { Report as MReport } from '@/models/report';
import type { Study } from '@/models/study';
import styles from './AddendumModal.module.css';

const { Text } = Typography;
const { TextArea } = Input;

export interface Addendum {
  author: string;
  reason?: string;
  createdAt?: string;
  text: string;
}

export interface AddendumModalProps {
  report: MReport | null;
  open: boolean;
  onCancel: () => void;
  onOk: (payload: { reason: string; text: string }) => void;
  study: Study;
  previousAddendums: Addendum[];
  loading?: boolean;
}

export default function AddendumModal({
  report,
  open,
  onCancel,
  onOk,
  study,
  previousAddendums,
  loading = false,
}: AddendumModalProps) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      reason: 'CONCURRENCE',
      text: '',
    });
  }, [open, form]);

  const studyId = report?.studyUuid ?? report?.uuid ?? '';
  const studyDate = study.updatedAt
    ? moment(study.updatedAt).format('MMM D, YYYY')
    : '';
  const studyType = study.modality || '';
  const patient =
    study?.patientFirstName && study?.patientLastName
      ? `${study.patientFirstName} ${study.patientLastName}`
      : '';

  const handleFinish = (values: { reason: string; text: string }) => {
    onOk({ reason: values.reason, text: values.text });
  };

  return (
    <Modal
      title="Add Report Addendum"
      open={open}
      onCancel={(e) => {
        e.stopPropagation();
        form.resetFields();
        onCancel();
      }}
      onOk={() => form.submit()}
      okText="Add Addendum"
      loading={loading}
      width={850}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={16}>
          <Col span={12}>
            <Space
              direction="vertical"
              size="small"
              className={styles.fullWidth}
            >
              <Text strong>Study ID</Text>
              <div>{studyId}</div>

              <Text strong>Study Date</Text>
              <div>{studyDate}</div>

              <Text strong>Study Type</Text>
              <div>{studyType}</div>

              <Text strong>Patient</Text>
              <div>{patient}</div>
            </Space>
          </Col>

          <Col span={12}>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={<Text strong>Reason</Text>}
                  name="reason"
                  rules={[
                    { required: true, message: 'Please select a reason' },
                  ]}
                >
                  <Radio.Group>
                    <Radio value="CONCURRENCE">Concur</Radio>
                    <Radio value="ADDITIONS">Additions/changes</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label={<Text strong>Addendum</Text>}
                  name="text"
                  rules={[
                    {
                      validator: async (_rule, value: string | undefined) => {
                        const trimmed = value?.trim();
                        if (!trimmed) {
                          return;
                        }
                        if (trimmed.length < 10) {
                          throw new Error(
                            'Addendum must be at least 10 characters'
                          );
                        }
                      },
                    },
                  ]}
                >
                  <TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Divider />
        <Row>
          <Col span={24}>
            <Text strong>Previous Addendum</Text>
            <List
              dataSource={previousAddendums}
              locale={{ emptyText: 'No previous addendums' }}
              renderItem={(item: Addendum) => (
                <List.Item>
                  <div className={styles.fullWidth}>
                    <Flex justify="space-between" vertical>
                      <div>
                        <div className={styles.addendumAuthor}>
                          {item.author || 'Unknown'}{' '}
                          {item.reason ? `(${item.reason})` : ''}
                        </div>
                        <div className={styles.addendumDate}>
                          {item.createdAt
                            ? moment(item.createdAt).format(
                                'MMM D, YYYY h:mm:ss A'
                              )
                            : ''}
                        </div>
                      </div>
                    </Flex>
                    <div className={styles.addendumText}>{item.text || ''}</div>
                  </div>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
